import React, { useMemo } from 'react';
import './StylePanel.css';

const defaultStyle = {
  enabled: true,
  bgColor: '#ffffff',
  padding: 16,
  radius: 12,
  borderWidth: 1,
  borderColor: '#E5E7EB',
  shadow: 'soft', // none | soft | medium
  animation: 'none', // none | fade | slide-up
  animationDuration: 400,
};

const defaultTitleConfig = {
  enabled: false,
  chinese: '',
  english: '',
  bgColor: '#1e90ff',
  textColor: '#ffffff',
};

export default function StylePanel({ value, onChange, onCopyToSameType, onCopyToAll, onResetToTemplateDefault, showTitleConfig = false }) {
  const v = useMemo(() => ({ ...defaultStyle, ...(value || {}) }), [value]);
  
  // 标题配置（如果组件支持）
  const titleConfig = useMemo(() => {
    return { ...defaultTitleConfig, ...(value?.titleConfig || {}) };
  }, [value]);

  const update = (patch) => {
    onChange?.({ ...v, ...patch });
  };

  const updateTitleConfig = (patch) => {
    onChange?.({ ...v, titleConfig: { ...titleConfig, ...patch } });
  };

  const reset = () => onChange?.({ ...defaultStyle });
  const disable = () => onChange?.({ ...v, enabled: false });
  
  const resetTitleConfig = () => {
    onChange?.({ ...v, titleConfig: { ...defaultTitleConfig } });
  };
  
  const disableTitleConfig = () => {
    onChange?.({ ...v, titleConfig: { ...titleConfig, enabled: false } });
  };

  // 计算预览样式
  const previewStyle = useMemo(() => {
    if (!v.enabled) return null;
    
    const boxShadow = v.shadow === 'none' ? 'none' : 
                      v.shadow === 'medium' ? '0 8px 24px rgba(0,0,0,0.15)' : 
                      '0 4px 12px rgba(0,0,0,0.08)';
    
    return {
      backgroundColor: v.bgColor || '#ffffff',
      padding: `${v.padding ?? 16}px`,
      borderRadius: `${v.radius ?? 12}px`,
      border: (v.borderWidth || 0) > 0 ? `${v.borderWidth}px solid ${v.borderColor || '#E5E7EB'}` : 'none',
      boxShadow,
    };
  }, [v]);

  return (
    <div className="style-panel">
      <div className="sp-header">
        <div className="sp-title">样式设置</div>
        <div className="sp-row">
          <input
            type="checkbox"
            id="sp-enabled"
            checked={!!v.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <label htmlFor="sp-enabled">启用自定义样式</label>
        </div>
      </div>

      {!v.enabled && (
        <div className="sp-disabled-hint">
          <p>样式功能已禁用。启用后可以自定义该内容块的背景、边距、边框、阴影等样式。</p>
        </div>
      )}

      {v.enabled && (
        <>
          {/* 预览区域 */}
          <div className="sp-preview-section">
            <div className="sp-preview-label">预览</div>
            <div className="sp-preview-box" style={previewStyle}>
              <div className="sp-preview-content">样式预览</div>
            </div>
          </div>

          {/* 基础样式 */}
          <div className="sp-section">
            <div className="sp-section-title">基础样式</div>
            <div className="sp-grid">
              <div className="sp-row">
                <label>背景颜色</label>
                <div className="sp-color-wrapper">
                  <input
                    type="color"
                    className="sp-color-input"
                    value={v.bgColor || '#ffffff'}
                    onChange={(e) => update({ bgColor: e.target.value })}
                  />
                  <span className="sp-color-value">{v.bgColor || '#ffffff'}</span>
                </div>
              </div>
              <div className="sp-row">
                <label>阴影</label>
                <select 
                  className="sp-select"
                  value={v.shadow || 'soft'} 
                  onChange={(e) => update({ shadow: e.target.value })}
                >
                  <option value="none">无</option>
                  <option value="soft">柔和</option>
                  <option value="medium">中等</option>
                </select>
              </div>
            </div>
          </div>

          {/* 尺寸和间距 */}
          <div className="sp-section">
            <div className="sp-section-title">尺寸和间距</div>
            <div className="sp-grid">
              <div className="sp-row">
                <label>内边距</label>
                <div className="sp-slider-wrapper">
                  <input
                    type="range"
                    className="sp-slider"
                    min="0"
                    max="48"
                    value={v.padding ?? 16}
                    onChange={(e) => update({ padding: parseInt(e.target.value || '0', 10) })}
                  />
                  <span className="sp-slider-value">{v.padding ?? 16}px</span>
                </div>
              </div>
              <div className="sp-row">
                <label>圆角</label>
                <div className="sp-slider-wrapper">
                  <input
                    type="range"
                    className="sp-slider"
                    min="0"
                    max="32"
                    value={v.radius ?? 12}
                    onChange={(e) => update({ radius: parseInt(e.target.value || '0', 10) })}
                  />
                  <span className="sp-slider-value">{v.radius ?? 12}px</span>
                </div>
              </div>
            </div>
          </div>

          {/* 边框 */}
          <div className="sp-section">
            <div className="sp-section-title">边框</div>
            <div className="sp-grid">
              <div className="sp-row">
                <label>边框宽度</label>
                <div className="sp-slider-wrapper">
                  <input
                    type="range"
                    className="sp-slider"
                    min="0"
                    max="6"
                    value={v.borderWidth ?? 1}
                    onChange={(e) => update({ borderWidth: parseInt(e.target.value || '0', 10) })}
                  />
                  <span className="sp-slider-value">{v.borderWidth ?? 1}px</span>
                </div>
              </div>
              <div className="sp-row">
                <label>边框颜色</label>
                <div className="sp-color-wrapper">
                  <input
                    type="color"
                    className="sp-color-input"
                    value={v.borderColor || '#E5E7EB'}
                    onChange={(e) => update({ borderColor: e.target.value })}
                  />
                  <span className="sp-color-value">{v.borderColor || '#E5E7EB'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 动画 */}
          <div className="sp-section">
            <div className="sp-section-title">动画效果</div>
            <div className="sp-grid">
              <div className="sp-row">
                <label>进入动画</label>
                <select 
                  className="sp-select"
                  value={v.animation || 'none'} 
                  onChange={(e) => update({ animation: e.target.value })}
                >
                  <option value="none">无</option>
                  <option value="fade">淡入</option>
                  <option value="slide-up">上移淡入</option>
                </select>
              </div>
              <div className="sp-row">
                <label>动画时长</label>
                <div className="sp-slider-wrapper">
                  <input
                    type="range"
                    className="sp-slider"
                    min="0"
                    max="2000"
                    step="100"
                    value={v.animationDuration ?? 400}
                    onChange={(e) => update({ animationDuration: parseInt(e.target.value || '0', 10) })}
                  />
                  <span className="sp-slider-value">{v.animationDuration ?? 400}ms</span>
                </div>
              </div>
            </div>
          </div>

          <div className="sp-actions">
            <button className="btn-link btn-link-primary" onClick={reset}>恢复默认</button>
            {onResetToTemplateDefault && (
              <button className="btn-link btn-link-warning" onClick={onResetToTemplateDefault}>恢复模板默认样式</button>
            )}
            <button className="btn-link btn-link-secondary" onClick={disable}>禁用样式</button>
            {onCopyToSameType && (
              <button className="btn-link btn-link-secondary" onClick={() => onCopyToSameType(v)}>复制到同类型</button>
            )}
            {onCopyToAll && (
              <button className="btn-link btn-link-secondary" onClick={() => onCopyToAll(v)}>复制到全部</button>
            )}
          </div>
        </>
      )}

      {/* 标题样式配置区域 */}
      {showTitleConfig && (
        <>
          <div className="sp-divider"></div>
          <div className="sp-header">
            <div className="sp-title">标题样式设置</div>
            <div className="sp-row">
              <input
                type="checkbox"
                id="sp-title-enabled"
                checked={!!titleConfig.enabled}
                onChange={(e) => updateTitleConfig({ enabled: e.target.checked })}
              />
              <label htmlFor="sp-title-enabled">启用标题自定义</label>
            </div>
          </div>

          {titleConfig.enabled && (
            <>
              <div className="sp-grid">
                <div className="sp-row sp-row-full">
                  <label>中文标题</label>
                  <input
                    type="text"
                    className="sp-input-text"
                    value={titleConfig.chinese || ''}
                    onChange={(e) => updateTitleConfig({ chinese: e.target.value })}
                    placeholder="产品基础信息"
                  />
                </div>
                <div className="sp-row sp-row-full">
                  <label>英文标题</label>
                  <input
                    type="text"
                    className="sp-input-text"
                    value={titleConfig.english || ''}
                    onChange={(e) => updateTitleConfig({ english: e.target.value })}
                    placeholder="Product Basic information."
                  />
                </div>
                <div className="sp-row">
                  <label>标题背景色</label>
                  <div className="sp-color-wrapper">
                    <input
                      type="color"
                      className="sp-color-input"
                      value={titleConfig.bgColor || '#1e90ff'}
                      onChange={(e) => updateTitleConfig({ bgColor: e.target.value })}
                    />
                    <span className="sp-color-value">{titleConfig.bgColor || '#1e90ff'}</span>
                  </div>
                </div>
                <div className="sp-row">
                  <label>标题文字色</label>
                  <div className="sp-color-wrapper">
                    <input
                      type="color"
                      className="sp-color-input"
                      value={titleConfig.textColor || '#ffffff'}
                      onChange={(e) => updateTitleConfig({ textColor: e.target.value })}
                    />
                    <span className="sp-color-value">{titleConfig.textColor || '#ffffff'}</span>
                  </div>
                </div>
              </div>

              <div className="sp-actions">
                <button className="btn-link btn-link-primary" onClick={resetTitleConfig}>恢复默认</button>
                <button className="btn-link btn-link-secondary" onClick={disableTitleConfig}>禁用标题样式</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
